import { cva } from 'class-variance-authority'
import { BlurView } from 'expo-blur'
import { type PropsWithChildren } from 'react'
import { RefreshControl, StyleSheet, View } from 'react-native'
import { type Action } from '~/components/Actions'
import AppActions from '~/components/AppActions'
import { ContextActionsMenu } from '~/components/layout/ContextActionsMenu'
import { ScrollView } from '~/components/ScrollView'
import { Heading1 } from '~/components/typography/Titles'
import { cn } from '~/lib/utils.ts'

export type ContentBoxVariant = 'default' | 'muted' | 'transparent'

export type ContentBoxProps = PropsWithChildren<{
  className?: string
  variant?: ContentBoxVariant
  scrollable?: boolean
  contentContainerClassName?: string
  footerClassName?: string
  headerClassName?: string
  header?: React.ReactNode
  footer?: React.ReactNode
  onRefresh?: () => void
  isRefreshing?: boolean
}>

export const contentBoxVariants = cva('relative flex-initial rounded-content', {
  variants: {
    variant: {
      default: 'bg-background',
      muted: 'bg-muted',
      transparent: 'bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export default function ContentBox({
  children,
  className,
  variant = 'default',
  scrollable = true,
  contentContainerClassName,
  footerClassName,
  footer,
  header,
  headerClassName,
  onRefresh,
  isRefreshing,
}: ContentBoxProps) {
  if (!scrollable) {
    return (
      <View
        className={cn(
          contentBoxVariants({ variant }),
          'relative mx-auto w-full max-w-xl overflow-hidden',
          className
        )}>
        {variant === 'transparent' && (
          <BlurView intensity={50} tint="extraLight" style={StyleSheet.absoluteFill} />
        )}
        {header && <View className={headerClassName}>{header}</View>}
        <View
          className={cn(
            'flex-initial px-5 py-10',
            !!header && 'pt-6',
            !!footer && 'pb-6',
            contentContainerClassName
          )}>
          {children}
        </View>
        {footer && <View className={cn('pb-6', footerClassName)}>{footer}</View>}
      </View>
    )
  }

  return (
    <View
      className={cn(
        contentBoxVariants({ variant }),
        'relative mx-auto w-full max-w-xl flex-1',
        className
      )}>
      <ScrollView
        footer={footer}
        footerClassName={footerClassName}
        offsetTrackHeight
        refreshControl={
          onRefresh ? (
            <RefreshControl refreshing={isRefreshing ?? false} onRefresh={onRefresh} />
          ) : undefined
        }
        className="flex-1">
        <View className="relative overflow-hidden rounded-content">
          {variant === 'transparent' && (
            <BlurView intensity={50} tint="extraLight" style={StyleSheet.absoluteFill} />
          )}
          {header && <View className={headerClassName}>{header}</View>}
          <View
            className={cn(
              'px-5 py-10',
              !!header && 'pt-6',
              !!footer && 'pb-6',
              contentContainerClassName
            )}>
            {children}
          </View>
        </View>
      </ScrollView>
    </View>
  )
}

export function BoxActionsHeader({
  children,
  className,
  variant = 'muted',
  actions,
  contextMenuActions,
  contentClassName,
  actionItemClassName: actionsClassName,
  contextMenuClassName,
  actionListClassName,
  toolbarClassName,
}: PropsWithChildren<{
  className?: string
  contentClassName?: string
  actions?: Action[]
  contextMenuActions?: Action[]
  variant?: ContentBoxVariant
  actionItemClassName?: string
  actionListClassName?: string
  contextMenuClassName?: string
  toolbarClassName?: string
}>) {
  return (
    <View
      className={cn(
        'isolated relative rounded-content',
        variant === 'muted' && 'bg-muted',
        className
      )}>
      <View
        className={cn(
          'order-2 w-full gap-2 border-b-2 px-4 py-6 text-center',
          variant === 'transparent' && 'border-neutral-400/30',
          variant === 'default' && 'border-border',
          variant === 'muted' && 'border-b border-[#D1D6F0]',
          (!actions || actions.length === 0) && 'border-b-0',
          contentClassName
        )}>
        {typeof children === 'string' ? (
          <Heading1 className="text-center">{children}</Heading1>
        ) : (
          children
        )}
      </View>
      {contextMenuActions && (
        <ContextActionsMenu
          actions={contextMenuActions}
          className={cn('absolute right-6 top-6 z-50 order-1', contextMenuClassName)}
        />
      )}
      {actions && (
        <AppActions
          variant={variant}
          className={cn('order-3 rounded-content', actionListClassName)}
          actionClassName={cn('rounded-t-none py-8', actionsClassName)}
          toolbarClassName={toolbarClassName}
          actions={actions}
        />
      )}
    </View>
  )
}
